import { Image } from "antd";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import entete from '../../../assets/img/entete_pool.png';
import { frenchDate, numberToWordsFrench } from "../../../variables/functions";
import { displayNumbers } from "../../../variables/numbersFunctions";
import { DocumentFooter } from "../../../components/docs/DocumentFooter";
import { getCompagnieLogo } from "../../../hooks/useFilesHooks";
import BarreTop from "../../../components/docs/BarreTop";
import SignatairesVisa from "./SignatairesVisa";
import { TYPE_DOC_SIGNATAIRE } from "../../../hooks/useSignataires";
import axiosInstance from "../../../variables/api/axiosInstance";
import { motifReglementAffiche } from "./groupeReglement";


const OrdreDeDepense = React.forwardRef((props, ref) => {
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
    const [pool, setPool] = useState({});
    useEffect(() => {
        if (props?.sinistre?.contrat?.organisme?.typeOrganisme !== "POOL") {
            getCompagnieLogo(props?.sinistre?.contrat?.organisme?.code)
                .then(async (response) => {
                    const imageBytes = await response.arrayBuffer();
                    var blob = new Blob([imageBytes], { type: "image/png" });
                    var imageUrl = URL.createObjectURL(blob);
                    setLogoPreviewUrl(imageUrl);
                })
                .catch((error) => {
                    console.log(error);
                });
        } else {
            axiosInstance.get("/api/organisme/pool")
                .then((response) => {
                    setPool(response.data);
                })
                .catch((error) => {
                    console.log(error);
                });
        }

        // Impression : forcer le FORMAT A4 (et pas seulement portrait). Sans ça, si le navigateur
        // est réglé sur « Letter » (279 mm), le contenu de 297 mm déborde sur une 2e page (pied seul).
        const style = document.createElement('style');
        style.innerHTML = `
            @media print {
                @page {
                    size: A4 portrait;
                    margin: 0;
                }
                html, body { margin: 0 !important; padding: 0 !important; }
            }
        `;
        document.head.appendChild(style);

        console.log("Decaissement:", props?.decaissement);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    const isPool = props?.sinistre?.contrat?.organisme?.typeOrganisme === "POOL";

    // AFFICHAGE « AD » (ayants droit) : le bénéficiaire (nom après « P/C ») est la personne de la VICTIME.
    // On affiche « AD » dès que CETTE victime est DÉCÉDÉE — déduit des victimes du sinistre (display-only,
    // sans toucher au flux de paiement). Repli sur l'ancien flag representantAyantDroit.
    const _benef = props?.decaissement?.beneficiaire;
    const _benefNom = `${_benef?.nom || ""} ${_benef?.prenom || ""}`.trim().toUpperCase();
    const _victimeDuBenef = (props?.sinistre?.victimes || []).find((v) =>
        (_benef?.codeId && v?.personne?.codeId && v.personne.codeId === _benef.codeId)
        || (!!_benefNom && `${v?.personne?.nom || ""} ${v?.personne?.prenom || ""}`.trim().toUpperCase() === _benefNom)
    );
    const _victimeDecedee = !!_victimeDuBenef && (
        _victimeDuBenef.decesSinistre === true
        || _victimeDuBenef.typeVictime === "DECEDE" || _victimeDuBenef.casVictime === "DECEDE"
    );
    const showAD = !!props?.decaissement?.representant
        && (props?.decaissement?.representantAyantDroit === true || _victimeDecedee);

    // Règlement groupé (aggregateGroupe) : une ligne du tableau par règlement du
    // groupe, avec les infos de son dossier. Quand le groupe couvre plusieurs
    // dossiers, le bloc d'infos mono-sinistre n'a plus de sens et est masqué.
    // N'afficher que les composantes RÉELLEMENT PAYÉES (montant > 0) : masque les lignes à 0
    // (ex. frais non payés) → pas de lignes « 0 » sur l'ordre de dépense.
    const lignesGroupe = (props?.decaissement?.lignes || []).filter((l) => Math.trunc(Number(l?.montant) || 0) > 0);
    const isGroupe = !!props?.decaissement?.isReglementMultiple && lignesGroupe.length > 0;
    const multiDossiers = (props?.decaissement?.dossiers?.length || 0) > 1;

    // Colonnes dossier d'une ligne du groupe : repli sur le sinistre du document
    // (impressions depuis le détail sinistre, où les lignes ne portent pas leur
    // propre objet sinistre — les helpers d'aggregateGroupe renvoient alors "—").
    const propsSin = props?.sinistre;
    const dossierLigne = (l) => ({
        numeroSinistre: l?.numeroSinistre && l.numeroSinistre !== "—"
            ? l.numeroSinistre
            : (propsSin?.numeroSinistreM || propsSin?.numSinistre || ""),
        dateSinistre: l?.dateSinistre || propsSin?.dateSinistre,
        numeroPolice: l?.numeroPolice && l.numeroPolice !== "—"
            ? l.numeroPolice
            : (propsSin?.contrat?.numeroPoliceM || propsSin?.contrat?.numeroPoliceP || ""),
        immatriculation: l?.immatriculation && l.immatriculation !== "—"
            ? l.immatriculation
            : (propsSin?.engin?.immatriculation || ""),
        assure: l?.assure && l.assure !== "—"
            ? l.assure
            : `${propsSin?.contrat?.souscripteur?.nom || ""} ${propsSin?.contrat?.souscripteur?.prenom || ""}`.trim(),
    });


    return (
        <div ref={ref} style={{
            width: "210mm",
            // 296 mm (et non 297) = 1 mm de sécurité sous la page A4 pour éviter qu'un arrondi
            // sub-pixel (bordures, pied absolu) ne fasse déborder d'un cheveu sur une 2e page.
            minHeight: "296mm",
            margin: "0 auto",
            backgroundColor: "white",
            fontFamily: "'Comic Sans MS', 'Comic Sans', cursive, sans-serif",
            fontSize: "12px",
            position: "relative",
            boxSizing: "border-box",
            // Sticky footer : le contenu pousse le pied de page tout en bas (margin-top:auto),
            // sans chevauchement, que le document tienne sur une ou plusieurs pages.
            display: "flex",
            flexDirection: "column"
        }}>
            {/* Header - Full Width */}
            <div style={{
                textAlign: "center",
                marginBottom: "10px",
                paddingBottom: "10px",
                paddingLeft: "0",
                paddingRight: "0"
            }}>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "0px 0px",
                    }}
                >
                    {props?.sinistre?.contrat?.organisme?.typeOrganisme !== "POOL" && (
                        <Image
                            src={logoPreviewUrl}
                            style={{
                                width: "100%",
                                height: "105px",
                            }}
                        />
                    )}

                    <Image
                        src={entete}
                        style={{
                            width: "100%",
                            height: "105px",
                        }}
                    />
                </div>
                <BarreTop />
            </div>

            {/* Content Area with Padding */}
            <div style={{ padding: "0 20mm" }}>
                {/* Title */}
                <div style={{
                    textAlign: "center",
                    marginBottom: "20px"
                }}>
                    <h2 style={{
                        fontSize: "22px",
                        textDecoration: "underline",
                        WebkitTextStroke: "0.5px black",
                        fontWeight: "bold",
                        margin: 0
                    }}>
                        Ordre de dépense
                    </h2>
                </div>

                {/* Info Table - Right Aligned. Masquée pour un règlement multiple/groupé :
                    les infos par dossier figurent dans le tableau des lignes. */}
                {!isGroupe && (
                    <div style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginBottom: "30px"
                    }}>
                        <table style={{
                            fontSize: "13px",
                            lineHeight: "1.8",
                            borderCollapse: "collapse",
                            width: "auto"
                        }} border={1}>
                            <tbody>
                                <tr>
                                    <td style={{ padding: "2px 8px", textAlign: "right", border: "1px solid black", whiteSpace: "nowrap" }}>Branche</td>
                                    {/* La BRANCHE (ex. VT/GUM), pas la catégorie (ex. VL/MO) ; repli catégorie si non chargée. */}
                                    <td style={{ padding: "2px 8px", textAlign: "right", border: "1px solid black" }}>{props?.sinistre?.engin?.categorie?.branche?.codeBranche || props?.sinistre?.engin?.categorie?.codeCategorie || ""}</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: "2px 8px", textAlign: "right", border: "1px solid black", whiteSpace: "nowrap" }}>Sinistre N°</td>
                                    <td style={{ padding: "2px 8px", textAlign: "right", border: "1px solid black" }}>{props?.sinistre?.numeroSinistreM || props?.sinistre?.numSinistre || ""}</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: "2px 8px", textAlign: "right", border: "1px solid black", whiteSpace: "nowrap" }}>Survenu le</td>
                                    <td style={{ padding: "2px 8px", textAlign: "right", border: "1px solid black" }}>{frenchDate(props?.sinistre?.dateSinistre) || ""}</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: "2px 8px", textAlign: "right", border: "1px solid black", whiteSpace: "nowrap" }}>Montant en FCFA</td>
                                    <td style={{ padding: "2px 8px", textAlign: "right", border: "1px solid black" }}>{displayNumbers(Math.trunc(props?.decaissement?.montantGlobal || 0))}</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: "2px 8px", textAlign: "right", border: "1px solid black", whiteSpace: "nowrap" }}>Retenue</td>
                                    <td style={{ padding: "2px 8px", textAlign: "right", border: "1px solid black" }}>
                                        {props?.decaissement?.montantPaye
                                            ? displayNumbers(
                                                parseFloat(props?.decaissement?.montantGlobal) -
                                                parseFloat(props?.decaissement?.montantPaye)
                                            )
                                            : "0"}
                                    </td>
                                </tr>
                                {/* Montant NET (= total − retenue) : affiché uniquement quand une retenue existe. */}
                                {(() => {
                                    const global = Math.trunc(parseFloat(props?.decaissement?.montantGlobal) || 0);
                                    const net = props?.decaissement?.montantPaye != null
                                        ? Math.trunc(parseFloat(props?.decaissement?.montantPaye) || 0)
                                        : global;
                                    const retenue = global - net;
                                    return retenue > 0 ? (
                                        <tr>
                                            <td style={{ padding: "2px 8px", textAlign: "right", border: "1px solid black", whiteSpace: "nowrap", fontWeight: "bold" }}>Montant net</td>
                                            <td style={{ padding: "2px 8px", textAlign: "right", border: "1px solid black", fontWeight: "bold" }}>{displayNumbers(net)}</td>
                                        </tr>
                                    ) : null;
                                })()}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Main Content */}
                <div style={{
                    fontSize: "13px",
                    lineHeight: "2",
                    marginBottom: "50px",
                    textAlign: "justify"
                }}>
                    <p style={{ margin: "0 0 20px 0" }}>
                        Veuillez payer à l'ordre de{" "}
                        <b style={{ textTransform: "uppercase", fontFamily: "'Comic Sans MS Bold', 'Comic Sans MS', sans-serif", WebkitTextStroke: "0.5px black", fontWeight: "bold" }}>
                            {props?.decaissement?.representant ? `${props.decaissement.representant} P/C ` : ""}
                        </b>
                        <b style={{ textTransform: "uppercase", fontFamily: "'Comic Sans MS Bold', 'Comic Sans MS', sans-serif", WebkitTextStroke: "0.5px black", fontWeight: "bold" }}>
                            {/* AD = ayants droit : affiché dès que la victime (= bénéficiaire après P/C) est
                                décédée. Voir showAD ci-dessus (déduit des victimes du sinistre, display-only). */}
                            {showAD ? "AD " : ""}
                            {
                                props?.decaissement?.beneficiaireMoral
                                    ? props.decaissement.beneficiaireMoral.nom
                                    : props?.decaissement?.beneficiaire?.prenom
                                        ? `${props.decaissement.beneficiaire.nom} ${props.decaissement.beneficiaire.prenom}`
                                        : props?.decaissement?.beneficiaire?.nom || ""
                            }
                        </b>
                    </p>
                    <p style={{ margin: "0 0 20px 0" }}>
                        La somme de{" "}
                        <b style={{ textTransform: "uppercase", fontFamily: "'Comic Sans MS Bold', 'Comic Sans MS', sans-serif", fontWeight: "700" }}>
                            {/* Somme NETTE réellement versée au bénéficiaire (montant global − retenue).
                                La retenue est prélevée (reversée au fisc), donc hors « somme à payer ». */}
                            {numberToWordsFrench((props?.decaissement?.montantPaye ?? props?.decaissement?.montantGlobal) || 0)}
                        </b> FCFA
                    </p>
                    <p style={{ margin: "0 0 20px 0" }}>
                        Représentant {motifReglementAffiche(props?.decaissement)}
                    </p>
                    {isGroupe && (
                        <p style={{ margin: "0 0 10px 0" }}>
                            {multiDossiers ? "des dossiers sinistres suivants :" : "du dossier sinistre suivant :"}
                        </p>
                    )}
                    {props?.decaissement?.formuleDeFin ? (
                        <p style={{
                            margin: "0 0 20px 0",
                            fontStyle: "italic",
                            textAlign: "justify",
                            // Formules de clôture en 2 paragraphes : préserver le saut de ligne.
                            whiteSpace: "pre-line"
                        }}>
                            {props.decaissement.formuleDeFin}
                        </p>
                    ) : null}
                </div>

                {/* Règlement groupé : une ligne par règlement avec les infos de son
                    dossier (modèle « ordre de dépense groupé » : N° Sinistre, date de
                    survenance, police, immatriculation, assuré, nature, montant). */}
                {isGroupe && (
                    <div style={{ margin: "-25px 0 20px 0" }}>
                        {/* table-layout fixed + coupure des contenus non sécables (police, immat) →
                            évite que le tableau déborde à droite de la page A4. */}
                        <style>{`.odd-grp-table{table-layout:fixed;}
                            .odd-grp-table th,.odd-grp-table td{word-break:break-word;overflow-wrap:anywhere;}`}</style>
                        <table className="odd-grp-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }} border={1}>
                            <thead>
                                <tr style={{ background: "#f0f0f0" }}>
                                    <th style={{ padding: "4px", border: "1px solid black", width: "17%" }}>N° Sinistre</th>
                                    <th style={{ padding: "4px", border: "1px solid black", width: "18%" }}>Date de surv.</th>
                                    <th style={{ padding: "4px", border: "1px solid black", width: "20%" }}>N° Police</th>
                                    <th style={{ padding: "4px", border: "1px solid black", width: "14%" }}>N° Imma</th>
                                    <th style={{ padding: "4px", border: "1px solid black", width: "17%" }}>Assuré</th>
                                    {/* <th style={{ padding: "4px", border: "1px solid black" }}>Nature</th> */}
                                    <th style={{ padding: "4px", border: "1px solid black", textAlign: "right", width: "14%" }}>Montant</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lignesGroupe.map((ligne, idx) => {
                                    const dossier = dossierLigne(ligne);
                                    return (
                                        <tr key={idx}>
                                            <td style={{ padding: "3px 4px", border: "1px solid black" }}>{dossier.numeroSinistre}</td>
                                            <td style={{ padding: "3px 4px", border: "1px solid black" }}>{dossier.dateSinistre ? frenchDate(dossier.dateSinistre) : ""}</td>
                                            <td style={{ padding: "3px 4px", border: "1px solid black" }}>{dossier.numeroPolice}</td>
                                            <td style={{ padding: "3px 4px", border: "1px solid black" }}>{dossier.immatriculation}</td>
                                            <td style={{ padding: "3px 4px", border: "1px solid black" }}>{dossier.assure}</td>
                                            {/* <td style={{ padding: "3px 4px", border: "1px solid black" }}>{ligne.label}</td> */}
                                            <td style={{ padding: "3px 4px", border: "1px solid black", textAlign: "right" }}>{displayNumbers(Math.trunc(ligne.montant || 0))}</td>
                                        </tr>
                                    );
                                })}
                                {(() => {
                                    const cell = { padding: "4px", border: "1px solid black", textAlign: "right" };
                                    const global = Math.trunc(parseFloat(props?.decaissement?.montantGlobal) || 0);
                                    const net = props?.decaissement?.montantPaye != null
                                        ? Math.trunc(parseFloat(props?.decaissement?.montantPaye) || 0)
                                        : global;
                                    const retenue = global - net;
                                    // Sans retenue (règlement normal) : un seul Total. Avec retenue (conseil
                                    // juridique / expert) : montant global (brut) → Retenue (info) → NET à payer.
                                    if (retenue <= 0) {
                                        return (
                                            <tr style={{ fontWeight: "bold" }}>
                                                <td colSpan={5} style={cell}>Total</td>
                                                <td style={cell}>{displayNumbers(global)}</td>
                                            </tr>
                                        );
                                    }
                                    return (
                                        <>
                                            <tr style={{ fontWeight: "bold" }}>
                                                <td colSpan={5} style={cell}>Total (montant global)</td>
                                                <td style={cell}>{displayNumbers(global)}</td>
                                            </tr>
                                            <tr>
                                                <td colSpan={5} style={cell}>Retenue</td>
                                                <td style={cell}>{displayNumbers(retenue)}</td>
                                            </tr>
                                            <tr style={{ fontWeight: "bold" }}>
                                                <td colSpan={5} style={cell}>Net à payer</td>
                                                <td style={cell}>{displayNumbers(net)}</td>
                                            </tr>
                                        </>
                                    );
                                })()}
                            </tbody>
                        </table>
                        {props?.decaissement?.hasProvision && (
                            <p style={{ marginTop: "8px", fontStyle: "italic", fontSize: "12px" }}>
                                NB : La provision est déductible de l'indemnité définitive.
                            </p>
                        )}
                    </div>
                )}

                {/* Date. Espaces verticaux réduits pour un règlement groupé (le tableau ajoute des
                    lignes) → garde tout le document sur une seule page A4. */}
                <div style={{
                    textAlign: "center",
                    fontSize: "13px",
                    marginBottom: isGroupe ? "45px" : "100px"
                }}>
                    Fait à Lomé, le {frenchDate(props?.decaissement?.createdAt || new Date())}
                </div>

                {/* Signatures PARAMÉTRABLES par organisme (fallback = signataires historiques). */}
                <SignatairesVisa
                    organismeCode={props?.sinistre?.contrat?.organisme?.code}
                    typeDocument={TYPE_DOC_SIGNATAIRE.ORDRE_DEPENSE}
                    decaissementCodeId={props?.decaissement?.codeId}
                    variant="grid"
                    style={{ gap: "80px", marginTop: isGroupe ? "30px" : "60px" }}
                    fallback={[
                        { titre: "Directeur Général", nom: isPool ? (pool?.responsable || "") : (props?.sinistre?.contrat?.organisme?.responsable || "-") },
                        { titre: isPool ? "Président du Conseil d'Encadrement" : "Chef Service Sinistres", nom: isPool ? "Yao Contant DJEKET" : "-" },
                    ]}
                />
            </div>

            {/* Footer - Full Width. margin-top:auto → poussé tout en bas (après les signatures),
                sans position absolue (qui chevauchait le contenu sur les documents multi-pages). */}
            <div style={{
                marginTop: "auto",
                fontSize: "1px",
                textAlign: "center",
                color: "#666",
                borderTop: "1px solid #ccc",
                paddingTop: "0px"
            }}>
                <DocumentFooter />
            </div>
        </div>
    );
});

OrdreDeDepense.propTypes = {
    decaissement: PropTypes.object, // peut porter `lignes` + `hasProvision` (règlement groupé)
    sinistre: PropTypes.object,
};

export default OrdreDeDepense;
